import Icon from '@xo-union/tk-component-icons';
import { format } from 'date-fns';
import pluralize from 'pluralize';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { IconName } from '@xo-union/dist-iconography';

import { GuestMessagingStatusesQuery } from '../../generated/graphql';
import LinkButton from '../LinkButton';
import useShareTracking from '../SharePage/useShareTracking';
import { editRsvpDeadlineReminderEmailRoute, editRsvpDeadlineReminderSmsRoute } from '../../helpers/routes';
import styles from './sentMessageItem.scss';

export type Batch = NonNullable<NonNullable<GuestMessagingStatusesQuery['guestMessagingStatuses']>[0]>;

interface Base {
  batchId: Batch['batchId'];
  sequenceDisplayName: Batch['sequenceDisplayName'];
  type: Batch['type'];
  sentDate?: Batch['sentDate'];
  onClickDetail?: (b: Batch) => void;
}

interface WGuests extends Base {
  guests: Batch['guests'];
  statusText?: never;
}

interface WStatus extends Base {
  guests?: never;
  statusText: string;
}

type Props = WGuests | WStatus;

export const SUCCESS_STATUSES = ['sent', 'delivered', 'opened'];
export const sendEmailRsvpReminderSeqId = '1a456305-52bd-4d75-8516-4263234e4416';
export const sendSmsRsvpReminderSeqId = '6a6c48b4-9495-4664-a14d-0622d3260a13';
type SeqType = 'email' | 'sms';

const SentMessageItem: React.FC<Props> = ({
  batchId,
  guests,
  statusText,
  type,
  sequenceDisplayName,
  sentDate = '',
  onClickDetail,
}) => {
  const passedGuests = guests?.filter(({ status }) => SUCCESS_STATUSES.includes(status));
  const repliedGuests = guests?.filter(({ reply }) => reply && reply !== '');
  const track = useShareTracking();
  const history = useHistory();

  const onClick = () => {
    if (onClickDetail && guests && passedGuests && repliedGuests) {
      track('Viewed Message Status', {
        messageMedium: type,
        messageType: sequenceDisplayName,
        messageSentCount: passedGuests.length,
        messageRepliesCount: repliedGuests.length,
        messageFailedCount: guests.length - passedGuests.length,
      });
      onClickDetail({ batchId, guests: guests || [], sequenceDisplayName, type, sentDate });
    }
  };

  const goToEditForm = (type: SeqType) => {
    track('WWS Share Interaction', { action: 'send rsvp' });

    const url =
      type === 'email'
        ? editRsvpDeadlineReminderEmailRoute(sendEmailRsvpReminderSeqId)
        : editRsvpDeadlineReminderSmsRoute(sendSmsRsvpReminderSeqId);

    history.push(url);
  };

  let replyNoun;
  if (sequenceDisplayName === 'Collect Contact Info') {
    replyNoun = 'submission';
  } else {
    replyNoun = 'reply';
  }

  return (
    <div className={styles.container} data-testid={`sent-message-${batchId}`}>
      <span className={styles.displayText}>{sequenceDisplayName}</span>
      {guests ? (
        <>
          <span className={styles.statusText} data-testid={`batch-guest-status-${batchId}`}>
            {sequenceDisplayName !== 'Remind Guests to RSVP' ? (
              <Icon name={`${type === 'email' ? 'email' : 'chat'}` as IconName} />
            ) : null}
            {passedGuests?.length} of {guests?.length} delivered
          </span>
          <span className={styles.statusText} data-testid={`batch-guest-replies-${batchId}`}>
            {repliedGuests?.length} {pluralize(replyNoun, repliedGuests?.length)}
          </span>
          <span className={styles.statusText} data-testid={`batch-sent-date-${batchId}`}>
            {sentDate ? format(new Date(sentDate), 'MMMM, dd yyyy') : 'Not Sent Yet'}
          </span>
        </>
      ) : (
        <span data-testid="batch-status-text" className={styles.statusText}>
          {statusText}
        </span>
      )}
      {onClickDetail ? (
        <LinkButton
          color="primary"
          className={styles.detailsButton}
          data-testid={`view-details-${batchId}`}
          onClick={onClick}
        >
          View Details
        </LinkButton>
      ) : (
        <LinkButton
          className={styles.detailsButton}
          data-testid={`edit-message-${batchId}`}
          onClick={() => goToEditForm(type as SeqType)}
        >
          Edit Message
        </LinkButton>
      )}
    </div>
  );
};

export default SentMessageItem;
